/**
 * @file Attachment preview modal and file-opening helpers.
 */

import {
    canPreviewAttachmentFile,
    isCsvAttachmentFile,
    isImageAttachmentFile,
    isMarkdownAttachmentFile,
    isOfficeAttachmentFile,
    isPdfAttachmentFile,
    isPlainTextAttachmentFile,
} from './attachmentModel.js';
import {
    escapeHTML,
} from '../utils/contentUtils.js';
import {
    renderMarkdownToHtml,
} from '../utils/markdownRenderer.js';

const CSV_PREVIEW_ROW_LIMIT = 500;
const TEXT_PREVIEW_CHAR_LIMIT = 200000;
const MARKDOWN_PREVIEW_CLASS = [
    'min-h-[320px] max-w-none p-5 text-sm leading-6 text-gray-800 dark:text-gray-100',
    '[&_a]:text-blue-600 [&_a]:underline dark:[&_a]:text-blue-400',
    '[&_blockquote]:my-3 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300',
    '[&_blockquote]:pl-4 [&_blockquote]:text-gray-600 dark:[&_blockquote]:border-gray-600',
    'dark:[&_blockquote]:text-gray-300',
    '[&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5',
    '[&_code]:font-mono [&_code]:text-[0.85em] dark:[&_code]:bg-gray-800',
    '[&_h1]:mb-3 [&_h1]:mt-0 [&_h1]:text-xl [&_h1]:font-semibold',
    '[&_h2]:mb-3 [&_h2]:mt-5 [&_h2]:text-lg [&_h2]:font-semibold',
    '[&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:text-base [&_h3]:font-semibold',
    '[&_hr]:my-4 [&_hr]:border-gray-200 dark:[&_hr]:border-gray-700',
    '[&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6',
    '[&_p]:my-3 [&_pre]:my-4 [&_pre]:overflow-auto [&_pre]:rounded-md',
    '[&_pre]:bg-gray-100 [&_pre]:p-3 dark:[&_pre]:bg-gray-800',
    '[&_table]:my-4 [&_table]:min-w-full [&_table]:border-separate [&_table]:border-spacing-0',
    '[&_td]:border-b [&_td]:border-gray-100 [&_td]:px-3 [&_td]:py-2',
    '[&_td]:text-xs dark:[&_td]:border-gray-800',
    '[&_th]:border-b [&_th]:border-gray-200 [&_th]:bg-gray-100 [&_th]:px-3',
    '[&_th]:py-2 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold',
    '[&_th]:text-gray-600 dark:[&_th]:border-gray-700 dark:[&_th]:bg-gray-800',
    'dark:[&_th]:text-gray-300 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6',
].join(' ');
const PREVIEW_DOWNLOAD_BUTTON_CLASS = [
    'standard-action-icon-btn hover:!text-blue-600 focus-visible:!text-blue-600',
    'dark:hover:!text-blue-400 dark:focus-visible:!text-blue-400',
].join(' ');
const PREVIEW_OPEN_BUTTON_CLASS = [
    'standard-action-icon-btn hover:!text-[var(--dfc-brand-primary)]',
    'focus-visible:!text-[var(--dfc-brand-primary)]',
].join(' ');
const PREVIEW_CLOSE_BUTTON_CLASS = [
    'standard-action-icon-btn standard-action-icon-btn--danger',
].join(' ');

/**
 * Escapes text for an HTML attribute.
 * @param {string} value Raw value.
 * @returns {string} Escaped value.
 */
function attrValue(value) {
    return escapeHTML(String(value ?? ''))
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Returns an existing preview modal or creates one.
 * @returns {HTMLElement} Modal element.
 */
function getPreviewModal() {
    let modal = document.getElementById('attachment-preview-modal');
    if (modal) {return modal;}

    modal = document.createElement('div');
    modal.id = 'attachment-preview-modal';
    modal.className = [
        'fixed',
        'inset-0',
        'z-[70]',
        'hidden',
        'items-center',
        'justify-center',
        'bg-black/60',
        'p-3',
        'sm:p-6',
    ].join(' ');
    modal.innerHTML = `
        <div class="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
            <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <div class="min-w-0">
                    <h3 id="attachment-preview-title" class="truncate text-base font-semibold text-gray-900 dark:text-gray-100"></h3>
                    <p id="attachment-preview-subtitle" class="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400"></p>
                </div>
                <div class="flex shrink-0 items-center gap-2">
                    <a id="attachment-preview-download" class="${PREVIEW_DOWNLOAD_BUTTON_CLASS}" target="_blank" rel="noopener" title="Download attachment" aria-label="Download attachment">
                        <span class="material-symbols-outlined text-[19px] leading-none">download</span>
                    </a>
                    <a id="attachment-preview-new-tab" class="${PREVIEW_OPEN_BUTTON_CLASS}" target="_blank" rel="noopener" title="Open in new tab" aria-label="Open in new tab">
                        <span class="material-symbols-outlined text-[19px] leading-none">open_in_new</span>
                    </a>
                    <button id="attachment-preview-close" type="button" class="${PREVIEW_CLOSE_BUTTON_CLASS}" aria-label="Close preview">
                        <span class="material-symbols-outlined text-[20px] leading-none">close</span>
                    </button>
                </div>
            </div>
            <div id="attachment-preview-body" class="min-h-[260px] flex-1 overflow-auto bg-gray-50 dark:bg-gray-900"></div>
        </div>
    `;
    document.body.appendChild(modal);

    modal.querySelector('#attachment-preview-close')?.addEventListener('click', closePreviewModal);

    return modal;
}

/**
 * Closes the attachment preview modal.
 */
function closePreviewModal() {
    const modal = document.getElementById('attachment-preview-modal');
    if (!modal) {return;}
    const body = modal.querySelector('#attachment-preview-body');
    if (body) {
        body.innerHTML = '';
    }
    modal.classList.add('hidden');
    modal.classList.remove('flex');
}

/**
 * Opens a preview modal with the supplied body markup.
 * @param {object} params Modal params.
 * @param {string} params.title Title.
 * @param {string} params.subtitle Subtitle.
 * @param {string} params.downloadUrl Download URL.
 * @param {string} params.openUrl Open URL.
 * @param {string} params.bodyHtml Body HTML.
 */
function openPreviewModal({
    title,
    subtitle = '',
    downloadUrl,
    openUrl,
    bodyHtml,
}) {
    const modal = getPreviewModal();
    modal.querySelector('#attachment-preview-title').textContent = title || 'Attachment';
    modal.querySelector('#attachment-preview-subtitle').textContent = subtitle;

    const downloadLink = modal.querySelector('#attachment-preview-download');
    const openLink = modal.querySelector('#attachment-preview-new-tab');
    downloadLink.href = downloadUrl || openUrl || '#';
    openLink.href = openUrl || downloadUrl || '#';

    const body = modal.querySelector('#attachment-preview-body');
    body.innerHTML = bodyHtml;

    modal.classList.remove('hidden');
    modal.classList.add('flex');
}

/**
 * Opens the attachment-style preview modal with caller-provided safe markup.
 * @param {object} params Modal params.
 * @param {string} params.title Title.
 * @param {string} params.subtitle Subtitle.
 * @param {string} params.downloadUrl Download URL.
 * @param {string} params.openUrl Open URL.
 * @param {string} params.bodyHtml Body HTML.
 */
export function openAttachmentPreviewModal(params) {
    openPreviewModal(params);
}

/**
 * Opens an attachment-style CSV preview from already-loaded text.
 * @param {object} params Preview params.
 * @param {string} params.title Preview title.
 * @param {string} params.csvText CSV text content.
 * @param {string} params.downloadUrl Download URL for the source CSV.
 * @param {string} params.openUrl Open URL for the source CSV.
 */
export function openCsvTextAttachmentPreview({
    title,
    csvText = '',
    downloadUrl = '',
    openUrl = '',
}) {
    const {rows, truncated} = parseCsvRows(csvText);
    openPreviewModal({
        title,
        subtitle: truncated ? `CSV preview: first ${CSV_PREVIEW_ROW_LIMIT} rows` : 'CSV preview',
        downloadUrl,
        openUrl,
        bodyHtml: renderCsvTable(rows),
    });
}

/**
 * Returns loading-state markup for preview setup.
 * @returns {string} Loading state HTML.
 */
function getPreviewLoadingHtml() {
    return `
        <div class="flex min-h-[320px] items-center justify-center p-6">
            <div class="flex flex-col items-center gap-3 text-center">
                <span class="material-symbols-outlined animate-pulse text-3xl text-gray-400 dark:text-gray-500">preview</span>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Preparing preview</p>
            </div>
        </div>
    `;
}

/**
 * Returns preview error markup.
 * @param {Error} error Preview error.
 * @returns {string} Error state HTML.
 */
function getPreviewErrorHtml(error) {
    return `
        <div class="flex min-h-[320px] items-center justify-center p-6">
            <div class="max-w-md text-center">
                <span class="material-symbols-outlined text-3xl text-gray-400 dark:text-gray-500">error</span>
                <p class="mt-3 text-sm font-semibold text-gray-800 dark:text-gray-100">Preview could not be loaded.</p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">${escapeHTML(error?.message || 'Use the download or open action instead.')}</p>
            </div>
        </div>
    `;
}

/**
 * Parses CSV text into rows, respecting quotes and escaped quotes.
 * @param {string} text CSV content.
 * @param {number} rowLimit Maximum row count.
 * @returns {{rows: Array<Array<string>>, truncated: boolean}} Parsed rows.
 */
function parseCsvRows(text, rowLimit = CSV_PREVIEW_ROW_LIMIT) {
    const rows = [];
    let row = [];
    let cell = '';
    let inQuotes = false;
    let truncated = false;

    for (let index = 0; index < text.length; index += 1) {
        const char = text[index];
        const next = text[index + 1];

        if (char === '"') {
            if (inQuotes && next === '"') {
                cell += '"';
                index += 1;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }

        if (!inQuotes && char === ',') {
            row.push(cell);
            cell = '';
            continue;
        }

        if (!inQuotes && (char === '\n' || char === '\r')) {
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
            if (char === '\r' && next === '\n') {
                index += 1;
            }
            if (rows.length >= rowLimit) {
                truncated = index < text.length - 1;
                break;
            }
            continue;
        }

        cell += char;
    }

    if (!truncated && (cell || row.length > 0)) {
        row.push(cell);
        rows.push(row);
    }

    return {rows, truncated};
}

/**
 * Renders CSV rows as a sanitized table.
 * @param {Array<Array<string>>} rows Parsed rows.
 * @returns {string} Table HTML.
 */
function renderCsvTable(rows) {
    if (!rows.length) {
        return '<div class="p-4 text-sm text-gray-500">No rows to preview.</div>';
    }

    const columnCount = Math.max(...rows.map((row) => row.length));
    const [headerRow, ...bodyRows] = rows;
    const headerHtml = Array.from({length: columnCount}, (_, index) => {
        return `<th class="border-b border-gray-200 bg-gray-100 px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">${escapeHTML(headerRow[index] || `Column ${index + 1}`)}</th>`;
    }).join('');
    const bodyHtml = bodyRows.map((row) => {
        const cells = Array.from({length: columnCount}, (_, index) => {
            return `<td class="border-b border-gray-100 px-3 py-2 text-xs text-gray-700 dark:border-gray-800 dark:text-gray-200">${escapeHTML(row[index] || '')}</td>`;
        }).join('');
        return `<tr>${cells}</tr>`;
    }).join('');

    return `
        <div class="overflow-auto">
            <table class="min-w-full border-separate border-spacing-0">
                <thead class="sticky top-0 z-10"><tr>${headerHtml}</tr></thead>
                <tbody>${bodyHtml}</tbody>
            </table>
        </div>
    `;
}

/**
 * Renders sanitized Markdown preview markup.
 * @param {string} markdownText Markdown content.
 * @returns {string} Sanitized Markdown preview HTML.
 */
function renderMarkdownPreview(markdownText) {
    return `<article class="${MARKDOWN_PREVIEW_CLASS}">${renderMarkdownToHtml(markdownText)}</article>`;
}

/**
 * Fetches text content for preview.
 * @param {string} url File URL.
 * @returns {Promise<string>} Text content.
 */
async function fetchText(url) {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Preview fetch failed with ${response.status}.`);
    }
    return await response.text();
}

/**
 * Opens the best available preview for an attachment.
 * @param {object} params Preview params.
 * @param {object} params.attachment Normalized attachment.
 * @param {Function} params.handleAttachmentOfficePreview Office preview callback.
 * @returns {Promise<void>}
 */
export async function openAttachmentPreview({
    attachment,
    handleAttachmentOfficePreview,
}) {
    const file = attachment?.currentVersion;
    if (!file?.url) {return;}

    const title = attachment.displayName || file.name || 'Attachment';
    const downloadUrl = file.url;

    if (!canPreviewAttachmentFile(file)) {
        window.open(file.url, '_blank', 'noopener');
        return;
    }

    openPreviewModal({
        title,
        subtitle: 'Preparing preview',
        downloadUrl,
        openUrl: file.url,
        bodyHtml: getPreviewLoadingHtml(),
    });

    try {
        if (isImageAttachmentFile(file)) {
            openPreviewModal({
                title,
                subtitle: 'Image preview',
                downloadUrl,
                openUrl: file.url,
                bodyHtml: `<div class="flex min-h-[320px] items-center justify-center p-4"><img src="${attrValue(file.url)}" alt="${attrValue(title)}" class="max-h-[75vh] max-w-full object-contain" /></div>`,
            });
            return;
        }

        if (isPdfAttachmentFile(file)) {
            openPreviewModal({
                title,
                subtitle: 'PDF preview',
                downloadUrl,
                openUrl: file.url,
                bodyHtml: `<iframe title="${attrValue(title)}" src="${attrValue(file.url)}" class="h-[75vh] w-full border-0 bg-white"></iframe>`,
            });
            return;
        }

        if (isCsvAttachmentFile(file)) {
            const csvText = await fetchText(file.url);
            const {rows, truncated} = parseCsvRows(csvText);
            openPreviewModal({
                title,
                subtitle: truncated ? `CSV preview: first ${CSV_PREVIEW_ROW_LIMIT} rows` : 'CSV preview',
                downloadUrl,
                openUrl: file.url,
                bodyHtml: renderCsvTable(rows),
            });
            return;
        }

        if (isMarkdownAttachmentFile(file)) {
            const markdownText = await fetchText(file.url);
            const truncated = markdownText.length > TEXT_PREVIEW_CHAR_LIMIT;
            const previewText = truncated ? markdownText.slice(0, TEXT_PREVIEW_CHAR_LIMIT) : markdownText;
            openPreviewModal({
                title,
                subtitle: truncated ? 'Markdown preview truncated' : 'Markdown preview',
                downloadUrl,
                openUrl: file.url,
                bodyHtml: renderMarkdownPreview(previewText),
            });
            return;
        }

        if (isPlainTextAttachmentFile(file)) {
            const text = await fetchText(file.url);
            const truncated = text.length > TEXT_PREVIEW_CHAR_LIMIT;
            const previewText = truncated ? text.slice(0, TEXT_PREVIEW_CHAR_LIMIT) : text;
            openPreviewModal({
                title,
                subtitle: truncated ? 'Text preview truncated' : 'Text preview',
                downloadUrl,
                openUrl: file.url,
                bodyHtml: `<pre class="min-h-[320px] whitespace-pre-wrap break-words p-4 text-sm leading-6 text-gray-800 dark:text-gray-100">${escapeHTML(previewText)}</pre>`,
            });
            return;
        }

        if (isOfficeAttachmentFile(file)) {
            const preview = await handleAttachmentOfficePreview({
                attachmentId: attachment.id,
                versionId: file.id,
                filePath: file.path,
            });
            openPreviewModal({
                title,
                subtitle: 'Office preview',
                downloadUrl,
                openUrl: preview.officeViewUrl,
                bodyHtml: `<iframe title="${attrValue(title)}" src="${attrValue(preview.officeEmbedUrl)}" class="h-[75vh] w-full border-0 bg-white"></iframe>`,
            });
        }
    } catch (error) {
        console.error('Attachment preview setup failed:', error);
        openPreviewModal({
            title,
            subtitle: 'Preview unavailable',
            downloadUrl,
            openUrl: file.url,
            bodyHtml: getPreviewErrorHtml(error),
        });
    }
}
